'use client';

import { Card, CardContent } from '#/components/ui/card';
import { Skeleton } from '#/components/ui/skeleton';
import { OrderStatusEnum } from '#/constants/order-status';
import {
  orderList_infiniteQueryFn,
  orderList_infiniteQueryKey,
} from '#/data/react-query/queries/order-list';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { Translation } from 'intl-lib';
import { HandCoins } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CreateEthereumStakingButton } from '../modules/create-ethereum-staking-button';
import { OrderRow } from './modules/order';
import { SearchParams, Toolbar } from './modules/toolbar';

export default function Page() {
  const [searchParams, setSearchParams] = useState<SearchParams>({
    searchKey: 'claimaddr',
    keywords: '',
  });
  const [filterStatus, setFilterStatus] = useState<OrderStatusEnum[]>([]);

  const {
    status,
    data,
    error,
    isFetching,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
    hasNextPage,
    hasPreviousPage,
  } = useInfiniteQuery({
    queryKey: orderList_infiniteQueryKey({
      status: filterStatus,
      searchKey: searchParams.searchKey,
      keywords: searchParams.keywords,
    }),
    queryFn: orderList_infiniteQueryFn,
    getNextPageParam: (lastPage, allPages) => lastPage.nextPageParam,
    initialPageParam: 0,
  });

  const allRows = data ? data.pages.flatMap(page => page.data ?? []) : [];

  const hasRows = allRows.length > 0;

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useWindowVirtualizer({
    count: hasNextPage ? allRows.length + 1 : allRows.length,
    estimateSize: () => 200,
    overscan: 5,
    scrollMargin: parentRef.current?.offsetTop ?? 0,
  });

  useEffect(() => {
    const lastItem = virtualizer.getVirtualItems().at(-1);

    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= allRows.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    allRows.length,
    isFetchingNextPage,
    virtualizer.getVirtualItems(),
  ]);

  const items = virtualizer.getVirtualItems();

  return (
    <div className="flex flex-col gap-3">
      <header className="flex flex-col @3xl/main:flex-row @3xl/main:items-center justify-between gap-3">
        <h3 className="text-sm font-medium truncate max-w-full inline-block">
          <Translation path="my_staking.order.title" />
        </h3>
        <Toolbar
          searchParams={searchParams}
          onSearchParamsChange={setSearchParams}
          filterStatus={filterStatus}
          onFilterStatusChange={setFilterStatus}
        />
      </header>
      {isLoading ? (
        <div className="flex flex-col gap-5 p-5 bg-white rounded-xl">
          <div className="flex justify-between">
            <Skeleton className="h-9 w-1/2 rounded-xl" />
            <Skeleton className="h-9 w-1/4 rounded-xl" />
          </div>
          <Skeleton className="h-18 w-full" />
        </div>
      ) : hasRows ? (
        <div
          ref={parentRef}
          className="relative"
          style={{
            height: virtualizer.getTotalSize(),
          }}
        >
          {items.map(virtualRow => {
            const isLoaderRow = virtualRow.index > allRows.length - 1;
            const item = allRows[virtualRow.index];
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className="absolute top-0 inset-x-0 py-1.5"
                style={{
                  // height: virtualRow.size,
                  transform: `translateY(${
                    virtualRow.start - virtualizer.options.scrollMargin
                  }px)`,
                }}
              >
                {isLoaderRow ? (
                  hasNextPage ? (
                    <div className="flex flex-col gap-5 p-5 bg-white rounded-xl">
                      <div className="flex justify-between">
                        <Skeleton className="h-9 w-1/2 rounded-xl" />
                        <Skeleton className="h-9 w-1/4 rounded-xl" />
                      </div>
                      <Skeleton className="h-18 w-full" />
                    </div>
                  ) : (
                    'Nothing more to load'
                  )
                ) : item != null ? (
                  <OrderRow item={item} />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="border-2 border-dashed border-slate-300">
          <CardContent className="flex flex-col items-center justify-center py-16 px-6">
            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-6">
              <HandCoins className="h-12 w-12 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              <Translation path="my_staking.order.empty_card.title" />
            </h3>
            <p className="text-slate-600 text-center mb-6 max-w-md">
              <Translation path="my_staking.order.empty_card.description" />
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <CreateEthereumStakingButton />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
